import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Rocket,
  Power,
  FileText,
  Clock,
  Trash2,
  Settings,
  TrendingUp,
  FolderOpen,
  ExternalLink,
  MoreHorizontal,
  Eye,
} from 'lucide-react';
import type { BlogPost, Blog, Site } from '../../schemas/validation';
import type { PublishStatus } from '../../hooks/useUnpublishedSummary';
import { cn } from '../../utils/cn';
import { getPublishedSiteUrl } from '../../utils/publishedUrl';
import { Tooltip } from '../../components/Tooltip';

interface BlogPostEditorHeaderProps {
  siteId: string;
  isNewPost: boolean;
  post: BlogPost | null;
  site: Site | null;
  blog: Blog | null;
  title: string;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  wordCount: number;
  readingTime: number;
  publishStatus?: PublishStatus;
  onSave: (publish: boolean) => void;
  onDelete: () => void;
  onTogglePublish: () => void;
  onShowSeoAnalysis: () => void;
  onShowMediaLibrary: () => void;
  onShowPreview: () => void;
}

export const BlogPostEditorHeader: React.FC<BlogPostEditorHeaderProps> = ({
  siteId,
  isNewPost,
  post,
  site,
  blog,
  title,
  hasUnsavedChanges,
  isSaving,
  isPublishing,
  wordCount,
  readingTime,
  publishStatus,
  onSave,
  onDelete,
  onTogglePublish,
  onShowSeoAnalysis,
  onShowMediaLibrary,
  onShowPreview,
}) => {
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setIsSettingsMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isPublished = publishStatus === 'up_to_date' || publishStatus === 'changed';

  const isViewableLive =
    site?.isPublished && site?.subdomain && blog?.isPublished && !!post?.publishedAt && post?.slug;

  const handleViewLive = () => {
    if (!isViewableLive || !site?.subdomain || !post?.slug) return;
    window.open(`${getPublishedSiteUrl(site)}/blog/${post.slug}`, '_blank');
  };

  const handleUnpublishConfirm = () => {
    setShowUnpublishConfirm(false);
    setIsSettingsMenuOpen(false);
    onTogglePublish();
  };

  // Publish CTA: show only when there's something to publish
  const showPublishCta =
    isNewPost || publishStatus === 'never_published' || publishStatus === 'changed';

  const publishCtaLabel = publishStatus === 'changed' ? 'Republish' : 'Publish';

  return (
    <>
      <nav className="bg-white border-b-2 border-gray-300 shadow-md px-4 py-3 sticky top-0 z-[60]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          {/* Left side: Back link and identity */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <Link
              to={`/sites/${siteId}/blog`}
              className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
              onClick={e => {
                if (
                  hasUnsavedChanges &&
                  !window.confirm('You have unsaved changes. Are you sure you want to leave?')
                ) {
                  e.preventDefault();
                }
              }}
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Blog</span>
            </Link>
            <div className="text-gray-300 hidden sm:block">|</div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {site?.name || 'Blog'}
              </h1>
              <div className="flex items-center space-x-1 text-sm text-gray-500">
                <span className="hidden sm:inline">Editing:</span>
                <span className="font-medium text-gray-700 truncate max-w-[120px] sm:max-w-none">
                  {isNewPost ? 'New Post' : title || 'Untitled'}
                </span>

                {/* Status chip — driven by publishStatus, no chip while loading */}
                {isNewPost && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 shrink-0">
                    Draft
                  </span>
                )}
                {!isNewPost && publishStatus === 'never_published' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 shrink-0">
                    Draft
                  </span>
                )}
                {publishStatus === 'changed' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 shrink-0">
                    Unpublished changes
                  </span>
                )}
                {publishStatus === 'up_to_date' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800 shrink-0">
                    Published
                  </span>
                )}

                {/* Unsaved indicator — subordinate to status chip */}
                {hasUnsavedChanges && (
                  <span className="hidden sm:inline-flex items-center text-[10px] text-gray-400 shrink-0">
                    · Unsaved
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
            {/* Word count / Reading time - hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-3 text-sm text-gray-500 mr-4">
              <span className="flex items-center space-x-1">
                <FileText size={14} />
                <span>{wordCount} words</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock size={14} />
                <span>{readingTime} min read</span>
              </span>
            </div>

            {/* Settings dropdown (gear) */}
            <div className="relative" ref={settingsMenuRef}>
              <Tooltip content="Post Settings">
                <button
                  onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
                  className="p-2 text-gray-600 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                  aria-expanded={isSettingsMenuOpen}
                  aria-haspopup="true"
                >
                  <Settings size={20} />
                </button>
              </Tooltip>

              {isSettingsMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* Unpublish — only when published */}
                  {isPublished && (
                    <button
                      onClick={() => setShowUnpublishConfirm(true)}
                      disabled={isPublishing}
                      className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center space-x-3 transition-colors disabled:opacity-50"
                    >
                      <Power size={16} />
                      <span>Unpublish</span>
                    </button>
                  )}

                  {/* Delete — only for existing posts */}
                  {!isNewPost && (
                    <button
                      onClick={() => {
                        setIsSettingsMenuOpen(false);
                        onDelete();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors"
                    >
                      <Trash2 size={16} />
                      <span>Delete Post</span>
                    </button>
                  )}

                  {/* Fallback when nothing to show (new post, no actions yet) */}
                  {isNewPost && !isPublished && (
                    <div className="px-4 py-2 text-sm text-gray-400">No additional actions</div>
                  )}
                </div>
              )}
            </div>

            {/* Preview */}
            <Tooltip content="Preview Post">
              <button
                onClick={onShowPreview}
                disabled={!title.trim()}
                className={cn(
                  'hidden sm:block p-2 rounded-lg transition-colors',
                  !title.trim()
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-blue-600 hover:text-white hover:bg-blue-600'
                )}
              >
                <Eye size={20} />
              </button>
            </Tooltip>

            {/* SEO Analysis */}
            <Tooltip content="SEO Analysis">
              <button
                onClick={onShowSeoAnalysis}
                className="hidden sm:block p-2 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors"
              >
                <TrendingUp size={20} />
              </button>
            </Tooltip>

            {/* Media Library */}
            <Tooltip content="Media Library">
              <button
                onClick={onShowMediaLibrary}
                className="hidden sm:block p-2 text-amber-600 hover:text-white hover:bg-amber-600 rounded-lg transition-colors"
              >
                <FolderOpen size={20} />
              </button>
            </Tooltip>

            {/* View Live */}
            {!isNewPost && isViewableLive && (
              <Tooltip content="View Live Post">
                <button
                  onClick={handleViewLive}
                  className="hidden sm:block p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-colors"
                >
                  <ExternalLink size={20} />
                </button>
              </Tooltip>
            )}

            {/* More menu - visible only on mobile */}
            <div className="relative sm:hidden" ref={moreMenuRef}>
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className="p-2 text-gray-600 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                aria-expanded={isMoreMenuOpen}
                aria-haspopup="true"
              >
                <MoreHorizontal size={20} />
              </button>

              {isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* Word count / Reading time */}
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center space-x-2">
                        <FileText size={12} />
                        <span>{wordCount} words</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock size={12} />
                        <span>{readingTime} min read</span>
                      </div>
                    </div>
                  </div>

                  {/* View Live */}
                  {!isNewPost && isViewableLive && (
                    <button
                      onClick={() => {
                        handleViewLive();
                        setIsMoreMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-purple-600 hover:bg-purple-50 flex items-center space-x-3"
                    >
                      <ExternalLink size={16} />
                      <span>View Live Post</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      onShowPreview();
                      setIsMoreMenuOpen(false);
                    }}
                    disabled={!title.trim()}
                    className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center space-x-3 disabled:opacity-50"
                  >
                    <Eye size={16} />
                    <span>Preview Post</span>
                  </button>

                  <button
                    onClick={() => {
                      onShowSeoAnalysis();
                      setIsMoreMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <TrendingUp size={16} className="text-indigo-600" />
                    <span>SEO Analysis</span>
                  </button>

                  <button
                    onClick={() => {
                      onShowMediaLibrary();
                      setIsMoreMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center space-x-3"
                  >
                    <FolderOpen size={16} />
                    <span>Media Library</span>
                  </button>
                </div>
              )}
            </div>

            {/* Save button — icon-row style */}
            <Tooltip content="Save (not live yet)">
              <button
                onClick={() => onSave(false)}
                disabled={!hasUnsavedChanges || isSaving || !title.trim()}
                className={cn(
                  'inline-flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium',
                  !hasUnsavedChanges || !title.trim()
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:text-white hover:bg-gray-600'
                )}
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={18} />
                    <span className="hidden sm:inline">Save</span>
                  </>
                )}
              </button>
            </Tooltip>

            {/* Publish / Republish CTA — hidden when up-to-date or loading for existing posts */}
            {showPublishCta && (
              <Tooltip content={publishCtaLabel}>
                <button
                  onClick={() => onSave(true)}
                  disabled={isPublishing || !title.trim()}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {isPublishing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span className="hidden sm:inline">Publishing...</span>
                    </>
                  ) : (
                    <>
                      <Rocket size={16} />
                      <span className="hidden sm:inline">{publishCtaLabel}</span>
                    </>
                  )}
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </nav>

      {/* Unpublish Confirmation Modal */}
      {showUnpublishConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unpublish Post</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will remove the post from your live blog, RSS feed, and its public URL. The post
              content will be preserved as a draft.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUnpublishConfirm(false);
                  setIsSettingsMenuOpen(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUnpublishConfirm}
                disabled={isPublishing}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isPublishing ? 'Unpublishing...' : 'Unpublish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
